import { FC, Ref, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import {
  addressAsString,
  buildAddressFromComponents,
  Button,
  Icon,
  IconSvg,
  ILocalAddress,
  useData,
} from '@/components';
import { useEcomStoreSelector, useUiStore, useUiStoreSelector } from '@/data';
import { Geoloc, TreezStore as TreezStoreType } from '@/types';
import {
  compareDistances,
  currencyFormat,
  getDeliveryScheduleByAddress,
  mapStoreToDistance,
} from '@/utils';
import {
  useActiveHeaderSlice,
  useCurrentMinimumAmount,
  useIsServerSide,
  useSelectedStore,
} from '@/hooks';
import { useStoreLocatorContext } from './context';

import { DeliverySection } from './DeliverySection.components';
import { PickupSection } from './PickupSection.components';

import styles from './storelocator.module.scss';
import { StoreRadioItem } from '../TreezStore/TreezStore.components';

const envButtonLabel = process?.env?.NEXT_PUBLIC_STORE_LOCATOR_LABEL;

const StoreLocatorButton = () => {
  const { deliveryAndPickup, buttonLabel } = useStoreLocatorContext();
  const { isServerSide } = useIsServerSide();
  const [storeAddress, setStoreAddress] = useState('');
  const activeSlice = useActiveHeaderSlice();
  const isStoreLocatorSmall =
    activeSlice?.variation === 'withCenteredLogoAndLocatorFix';
  const label = envButtonLabel ?? buttonLabel;

  const { is_store_location_open, setState } = useUiStoreSelector([
    'is_store_location_open',
  ]);

  const { delivery_details, delivery_address } = useEcomStoreSelector([
    'delivery_details',
    'provider_inventory_location_id',
    'delivery_address',
  ]);

  const { store: selectedStore } = useSelectedStore();
  const { currentMinAmount } = useCurrentMinimumAmount();

  useEffect(() => {
    if (delivery_details.pickup && selectedStore) {
      setStoreAddress(selectedStore?.full_address);
      return;
    }

    if (delivery_details.delivery && delivery_address) {
      setStoreAddress(addressAsString(delivery_address as ILocalAddress));
    }
  }, [
    delivery_details.delivery,
    delivery_details.pickup,
    delivery_address,
    selectedStore,
  ]);

  const minimumTotalLabel = selectedStore?.delivery_minimum_total
    ? 'Grand Total'
    : 'Subtotal';

  return (
    <Button
      ariaLabel="Open store selector and purchase method"
      className={classNames(styles.store_locator__button, {
        [styles.store_locator__button_header_variation]: isStoreLocatorSmall,
      })}
      onClick={() =>
        setState({
          is_store_location_open: !is_store_location_open,
        })
      }
      size="md"
    >
      <div className={styles['button-content']}>
        <div
          className={classNames(styles.shopping_mode_indicator, {
            [styles.shopping_mode_indicator_small]: isStoreLocatorSmall,
            [styles.shopping_mode_indicator__no_store]:
              isServerSide || !selectedStore,
            [styles.single_mode]: !deliveryAndPickup,
          })}
        >
          {isServerSide || !selectedStore ? (
            <IconSvg name="marker" />
          ) : (
            <>
              <IconSvg name={delivery_details.pickup ? 'store' : 'car'} />
              <span className={styles.shopping_mode_indicator__text}>
                {delivery_details.pickup ? 'Pickup' : 'Delivery'}
              </span>
              {deliveryAndPickup && <IconSvg name="chevron-right" />}
            </>
          )}
        </div>
        <div
          className={classNames(styles.info, {
            [styles.info__horizontal]: isStoreLocatorSmall,
          })}
        >
          {(isServerSide || !selectedStore) && (
            <span
              className={classNames(styles.method, styles.method_button_label)}
            >
              {label}
            </span>
          )}
          {!isServerSide && selectedStore && (
            <span className={styles.method}>
              {delivery_details.pickup
                ? `${selectedStore?.name}`
                : `Delivery to ${
                    currentMinAmount == 0
                      ? ''
                      : `($${currencyFormat(
                          currentMinAmount,
                        )} ${minimumTotalLabel} min)`
                  } `}
            </span>
          )}
          {!isServerSide && isStoreLocatorSmall && selectedStore && (
            <span>-</span>
          )}
          {!!storeAddress && (
            <span className={styles.address} title={storeAddress}>
              {storeAddress}
            </span>
          )}
        </div>
        {(isServerSide || !selectedStore) && (
          <span className={styles.decorator}>
            <IconSvg name="chevron-right" />
          </span>
        )}
      </div>
    </Button>
  );
};

const StoreLocatorTop = () => {
  const [, setState] = useUiStore();
  const {
    onlyDelivery,
    onlyPickup,
    title,
    pickupSectionTitle,
    deliverySectionTitle,
  } = useStoreLocatorContext();

  const panelTitle = onlyPickup
    ? pickupSectionTitle
    : onlyDelivery
    ? deliverySectionTitle
    : title;

  return (
    <div className={styles.store__locator_top}>
      <div className={styles.store__locator_header}>
        <h6>{panelTitle}</h6>
        <Button
          ariaLabel="Close"
          className={styles.close_button}
          icon
          onClick={() => {
            setState({ is_store_location_open: false });
          }}
        >
          <Icon className={styles.close_icon} name="close" />
        </Button>
      </div>
      <ShopModeSelector />
    </div>
  );
};

interface IShopModeSelectorProps {
  className?: string;
}

const ShopModeSelector: FC<IShopModeSelectorProps> = ({ className }) => {
  const { deliveryAndPickup, isDelivery, setIsDelivery, setIsPickup } =
    useStoreLocatorContext();
  // const { provider_inventory_location_id } = useEcomStoreSelector([
  //   'provider_inventory_location_id',
  // ]);

  // const isExpressDelivery = Boolean(provider_inventory_location_id);

  const handleDeliverySelection = () => {
    setIsDelivery(true);
    setIsPickup(false);
  };

  const handlePickupSelection = () => {
    setIsDelivery(false);
    setIsPickup(true);
  };

  if (!deliveryAndPickup) {
    return null;
  }

  return (
    <div className={classNames(className, styles.shop_mode)}>
      <div
        className={classNames(styles.shop_mode__background, {
          [styles.delivery]: isDelivery,
          [styles.pickup]: !isDelivery,
        })}
      />
      <Button
        ariaLabel="Delivery"
        className={classNames(styles.delivery_mode, {
          [styles.selected]: isDelivery,
        })}
        onClick={handleDeliverySelection}
      >
        <IconSvg name="car" />
        <span>Delivery</span>
      </Button>
      <Button
        ariaLabel="Pickup"
        className={classNames(styles.pickup_mode, {
          [styles.selected]: !isDelivery,
        })}
        onClick={handlePickupSelection}
      >
        <IconSvg name="store" />
        <span>Pickup</span>
      </Button>
    </div>
  );
};

interface IStoreLocatorCenterProps {
  scrollListToTop: () => void;
  ref?: Ref<HTMLDivElement | null>;
}

const StoreLocatorCenter: FC<IStoreLocatorCenterProps> = ({
  scrollListToTop,
  ref: storesListRef,
}) => {
  const {
    isDelivery,
    isPickup,
    singleStore,
    onlyDelivery,
    onlyPickup,
    setDeliveryAddress,
    setSelectedStoreId,
    stores,
    setStores,
    setIsDeliveryAddressInvalid,
  } = useStoreLocatorContext();

  const [loadingLocation, setLoadingLocation] = useState(false);

  const showInstructionText = useMemo(
    () => !singleStore && !onlyDelivery && !onlyPickup,
    [singleStore, onlyDelivery, onlyPickup],
  );

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | undefined>(
    undefined,
  );

  const { store } = useData();

  const onClickUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      inputRef.current?.focus();
      return;
    }

    const onSuccess = async position => {
      const { latitude: lat, longitude: lng } = position.coords;
      const geocoder = new google.maps.Geocoder();

      try {
        setDeliveryAddress(undefined);

        if (isPickup) {
          // the stores are sorted also selected store
          // and moved to the top
          sortStoreByLatAndLngAndSelectStore(stores, { lat, lng });
        }

        geocoder.geocode(
          {
            location: {
              lat,
              lng,
            },
          },
          res => {
            const bestResult =
              res?.find(result => result.types.includes('street_address')) ??
              res?.[0];

            if (!bestResult) {
              onError('Unable to get the current location.');
              return;
            }

            const builtAddress = buildAddressFromComponents(bestResult);

            // if is pickup, set the delivery address and return the store is selected on
            // sortStoreByLatAndLngAndSelectStore method above
            if (isPickup) {
              setDeliveryAddress({
                ...builtAddress,
                latitude: lat,
                longitude: lng,
              });

              setLoadingLocation(false);
              return;
            }

            // if is delivery, check if the zip code is valid and
            // select a delivery zone
            const ds = getDeliveryScheduleByAddress(builtAddress, store);

            if (!ds) {
              setLoadingLocation(false);
              setIsDeliveryAddressInvalid(true);
              return;
            }

            setSelectedStoreId(ds?.stores_provider_id);
            setDeliveryAddress({
              ...builtAddress,
              latitude: lat,
              longitude: lng,
            });

            setLoadingLocation(false);
          },
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        onError(e);
      }
    };

    const onError = error => {
      // eslint-disable-next-line no-console
      console.log(error);

      setLoadingLocation(false);
      setIsDeliveryAddressInvalid(false);
    };

    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 5000,
    });
  };

  const sortStoreByLatAndLngAndSelectStore = (
    stores: TreezStoreType[],
    currentLocation: Geoloc,
  ) => {
    Promise.all(stores.map(mapStoreToDistance(currentLocation)))
      .then(stores => {
        const sortedStores = stores.sort(compareDistances);

        setSelectedStoreId(sortedStores[0].shortName);

        scrollListToTop();
        setStores(sortedStores);
        setLoadingLocation(false);
      })
      .catch(() => {
        setLoadingLocation(false);
      });
  };

  return (
    <div className={styles.store_locator_center} ref={storesListRef}>
      {isDelivery && (
        <DeliverySection
          handleUseCurrentLocation={onClickUseCurrentLocation}
          loadingLocation={loadingLocation}
          ref={inputRef as Ref<HTMLInputElement>}
          showInstructionText={showInstructionText}
        />
      )}

      {isPickup && (
        <PickupSection
          handleUseCurrentLocation={onClickUseCurrentLocation}
          loadingLocation={loadingLocation}
          showInstructionText={showInstructionText}
        />
      )}
    </div>
  );
};

interface IStoreExternalItemProps {
  isOpen: boolean;
  isSelected: boolean;
  store: any;
  // eslint-disable-next-line no-unused-vars
  onSelected: (id?: string) => void;
}

const StoreExternalItem: FC<IStoreExternalItemProps> = ({
  isOpen,
  isSelected,
  store,
  onSelected,
}) => {
  const handleStoreSelected = async () => {
    onSelected(store?.shortName);
  };

  return (
    <StoreRadioItem
      hiddeStatus
      isOpen={isOpen}
      isSelected={isSelected}
      onSelected={handleStoreSelected}
      store={store}
    />
  );
};

// this component is used to create an overlay when the store locator
// is open to force the user to select an store
const Overlay = () => {
  const { showOverlay } = useStoreLocatorContext();

  return showOverlay ? <div className={styles.store_locator__overlay} /> : null;
};

export {
  StoreLocatorTop,
  StoreLocatorButton,
  ShopModeSelector,
  StoreLocatorCenter,
  Overlay,
  StoreExternalItem,
};
